import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  fetchGuardianConsent,
  fetchGuardianConsentDocumentPreview,
  submitGuardianConsent,
} from "../../api/guardianConsent";
import { ApiError } from "../../api/http";
import SignaturePad from "../../components/guardian/SignaturePad";
import logoMain from "../../assets/img/logo_main.png";
import { GuardianConsentDetail } from "../../types/guardianConsent";

const RELATION_OPTIONS = ["부", "모", "기타"] as const;
const CERT_ACCEPT = "image/*,.pdf,application/pdf";
const MAX_CERT_BYTES = 20 * 1024 * 1024;

const formatResidentIdMasked = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 7);
  if (!digits) {
    return "";
  }
  if (digits.length <= 6) {
    return digits;
  }
  return `${digits.slice(0, 6)}-${digits.slice(6, 7)}${"*".repeat(6)}`;
};

const isValidResidentIdMasked = (value: string): boolean =>
  /^\d{6}-[1-8]\*{6}$/.test(value.trim());

type ToastState = { type: "success" | "error"; message: string } | null;

const GuardianConsentPage = () => {
  const { linkId = "" } = useParams<{ linkId: string }>();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState<GuardianConsentDetail | null>(null);
  const [loadError, setLoadError] = useState<{ status: number; message: string } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  const [guardianName, setGuardianName] = useState("");
  const [relation, setRelation] = useState("");
  const [relationOther, setRelationOther] = useState("");
  const [residentIdMasked, setResidentIdMasked] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPreviewUrl, setCertPreviewUrl] = useState<string | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewText, setPreviewText] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const certInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!toast) {
      return;
    }
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    return () => {
      if (certPreviewUrl) {
        URL.revokeObjectURL(certPreviewUrl);
      }
    };
  }, [certPreviewUrl]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!linkId) {
        setLoadError({ status: 404, message: "유효하지 않은 링크입니다." });
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);
      try {
        const data = await fetchGuardianConsent(linkId);
        if (cancelled) {
          return;
        }
        setDetail(data);
        if (data.status === "submitted") {
          setSubmitted(true);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof ApiError) {
          setLoadError({
            status: error.status,
            message:
              error.status === 404
                ? "유효하지 않은 링크입니다."
                : error.status === 410
                  ? "동의 링크가 만료되었거나 철회되었습니다."
                  : error.message,
          });
        } else {
          setLoadError({ status: 0, message: "정보를 불러오지 못했습니다." });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [linkId]);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
  };

  const openDocumentPreview = async () => {
    if (!linkId) {
      return;
    }
    setPreviewOpen(true);
    if (detail?.full_document_preview_text) {
      setPreviewText(detail.full_document_preview_text);
      return;
    }
    setPreviewLoading(true);
    try {
      const data = await fetchGuardianConsentDocumentPreview(linkId);
      setPreviewText(data.full_document_preview_text || "");
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "전문을 불러오지 못했습니다.";
      showToast("error", message);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const onCertChange = (file: File | null) => {
    if (certPreviewUrl) {
      URL.revokeObjectURL(certPreviewUrl);
      setCertPreviewUrl(null);
    }
    if (!file) {
      setCertFile(null);
      return;
    }
    if (file.size > MAX_CERT_BYTES) {
      showToast("error", "파일 용량은 20MB 이하여야 합니다.");
      return;
    }
    setCertFile(file);
    if (file.type.startsWith("image/")) {
      setCertPreviewUrl(URL.createObjectURL(file));
    }
  };

  const resolvedRelation =
    relation === "기타" ? relationOther.trim() : relation.trim();

  const validate = (): string | null => {
    if (!guardianName.trim()) {
      return "보호자 성명을 입력해 주세요.";
    }
    if (!resolvedRelation) {
      return "연소근로자와의 관계를 입력해 주세요.";
    }
    if (!isValidResidentIdMasked(residentIdMasked)) {
      return "주민등록번호 형식을 확인해 주세요. (예: 800101-1******)";
    }
    if (!phone.trim()) {
      return "연락처를 입력해 주세요.";
    }
    if (!address.trim()) {
      return "주소를 입력해 주세요.";
    }
    if (!signatureDataUrl) {
      return "서명을 입력해 주세요.";
    }
    if (!certFile) {
      return "가족관계증명서를 첨부해 주세요.";
    }
    return null;
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || detail?.status !== "pending") {
      return;
    }

    const validationError = validate();
    if (validationError) {
      showToast("error", validationError);
      return;
    }

    const fullAddress = [address.trim(), addressDetail.trim()].filter(Boolean).join(" ");
    const formData = new FormData();
    formData.append("guardian_name", guardianName.trim());
    formData.append("guardian_resident_id_masked", residentIdMasked.trim());
    formData.append("guardian_address", fullAddress);
    formData.append("guardian_phone_number", phone.trim());
    formData.append("relation_to_minor_worker", resolvedRelation);
    formData.append("guardian_signature_data_url", signatureDataUrl || "");
    if (certFile) {
      formData.append("family_relation_certificate_file", certFile);
    }

    setSubmitting(true);
    try {
      await submitGuardianConsent(linkId, formData);
      setSubmitted(true);
      setJustSubmitted(true);
      setDetail((prev) => (prev ? { ...prev, status: "submitted" } : prev));
      showToast("success", "동의가 완료되었습니다.");
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 409) {
          setSubmitted(true);
          setDetail((prev) => (prev ? { ...prev, status: "submitted" } : prev));
          showToast("error", "이미 제출되었거나 현재 동의할 수 없는 상태입니다.");
        } else if (error.status === 410) {
          setDetail((prev) => (prev ? { ...prev, status: "expired" } : prev));
          showToast("error", "동의 링크가 만료되었거나 철회되었습니다.");
        } else if (error.status === 413) {
          showToast("error", "파일 용량이 너무 큽니다. 20MB 이하로 다시 시도해 주세요.");
        } else {
          showToast("error", error.message || "제출에 실패했습니다.");
        }
      } else {
        showToast("error", "제출에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const statusMessage = (() => {
    if (loadError) {
      return {
        title: loadError.status === 404 ? "유효하지 않은 링크" : "동의 진행 불가",
        body: loadError.message,
      };
    }
    if (!detail) {
      return null;
    }
    if (justSubmitted) {
      return {
        title: "동의가 완료되었습니다",
        body: "보호자 동의가 정상적으로 제출되었습니다. 이 창을 닫으셔도 됩니다.",
      };
    }
    if (submitted || detail.status === "submitted") {
      return {
        title: "이미 제출됨",
        body: "이 링크로는 이미 보호자 동의가 제출되었습니다. 다시 제출할 수 없습니다.",
      };
    }
    if (detail.status === "expired") {
      return {
        title: "동의 링크가 만료되었습니다",
        body: "유효 기간이 지나 더 이상 동의할 수 없습니다. 사업주 또는 근로자에게 새 링크를 요청해 주세요.",
      };
    }
    if (detail.status === "revoked") {
      return {
        title: "동의 링크가 철회되었습니다",
        body: "이 동의 요청은 철회되어 더 이상 진행할 수 없습니다.",
      };
    }
    return null;
  })();

  const summary = detail?.summary;
  const legalNotice =
    detail?.legal_notice ||
    "근로기준법 제66조에 따라 만 18세 미만 연소자의 근로계약 체결에 대한 법정대리인 동의를 진행합니다.";

  return (
    <div className="guardian-page">
      <header className="guardian-page__header">
        <img className="guardian-page__logo" src={logoMain} alt="나눔" />
      </header>

      <main className="guardian-page__main">
        {loading ? (
          <div className="guardian-status">
            <p className="guardian-status__body">불러오는 중...</p>
          </div>
        ) : statusMessage ? (
          <div className="guardian-status">
            <h1 className="guardian-status__title">{statusMessage.title}</h1>
            <p className="guardian-status__body">{statusMessage.body}</p>
            {detail && (submitted || detail.status === "submitted") && (
              <div className="guardian-status__meta">
                {detail.branch_name && <span>점포: {detail.branch_name}</span>}
                {detail.minor_worker_name && <span>연소자: {detail.minor_worker_name}</span>}
              </div>
            )}
          </div>
        ) : (
          <form className="guardian-form" onSubmit={onSubmit} noValidate>
            <div className="guardian-form__intro">
              <h1 className="guardian-form__title">친권자(후견인) 인적사항</h1>
              <p className="guardian-form__notice">{legalNotice}</p>
              {(detail?.branch_name || detail?.minor_worker_name) && (
                <p className="guardian-form__context">
                  {detail.branch_name && <span>{detail.branch_name}</span>}
                  {detail.branch_name && detail.minor_worker_name && <span aria-hidden> · </span>}
                  {detail.minor_worker_name && <span>연소자 {detail.minor_worker_name}</span>}
                </p>
              )}
            </div>

            <section className="guardian-summary">
              <div className="guardian-summary__head">
                <h2>주요 근로 조건 요약</h2>
                <button className="guardian-summary__preview" type="button" onClick={openDocumentPreview}>
                  전문 보기
                  <span aria-hidden>›</span>
                </button>
              </div>
              <dl className="guardian-summary__list">
                <div>
                  <dt>근무기간</dt>
                  <dd>{summary?.work_period || "-"}</dd>
                </div>
                <div>
                  <dt>근무 시간</dt>
                  <dd>{summary?.work_schedule || "-"}</dd>
                </div>
                <div>
                  <dt>급여 조건</dt>
                  <dd>{summary?.wage_label || "-"}</dd>
                </div>
                <div>
                  <dt>근무 장소</dt>
                  <dd>{summary?.work_place || "-"}</dd>
                </div>
                <div>
                  <dt>담당 업무</dt>
                  <dd>{summary?.job_description || "-"}</dd>
                </div>
              </dl>
            </section>

            <label className="guardian-field">
              <span className="guardian-field__label">
                보호자(친권자) 성명 <i aria-hidden>*</i>
              </span>
              <input
                type="text"
                placeholder="이름을 입력해주세요."
                value={guardianName}
                onChange={(e) => setGuardianName(e.target.value)}
                autoComplete="name"
                required
              />
            </label>

            <label className="guardian-field">
              <span className="guardian-field__label">
                연소근로자와의 관계 <i aria-hidden>*</i>
              </span>
              <div className="guardian-field__select-wrap">
                <select
                  value={relation}
                  onChange={(e) => setRelation(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    연소근로자와의 관계를 선택해 주세요
                  </option>
                  {RELATION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            {relation === "기타" && (
              <label className="guardian-field">
                <span className="guardian-field__label">
                  관계 상세 <i aria-hidden>*</i>
                </span>
                <input
                  type="text"
                  placeholder="관계를 입력해 주세요"
                  value={relationOther}
                  onChange={(e) => setRelationOther(e.target.value)}
                  required
                />
              </label>
            )}

            <label className="guardian-field">
              <span className="guardian-field__label">
                주민등록번호 <i aria-hidden>*</i>
              </span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="주민등록번호를 입력해 주세요"
                value={residentIdMasked}
                onChange={(e) => setResidentIdMasked(formatResidentIdMasked(e.target.value))}
                autoComplete="off"
                required
              />
            </label>

            <label className="guardian-field">
              <span className="guardian-field__label">
                연락처 <i aria-hidden>*</i>
              </span>
              <input
                type="tel"
                placeholder="연락처를 입력해 주세요"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                required
              />
            </label>

            <div className="guardian-field-group">
              <label className="guardian-field">
                <span className="guardian-field__label">
                  주소 <i aria-hidden>*</i>
                </span>
                <input
                  type="text"
                  placeholder="주소를 입력해 주세요"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  autoComplete="street-address"
                  required
                />
              </label>
              <label className="guardian-field">
                <span className="visually-hidden">상세 주소</span>
                <input
                  type="text"
                  placeholder="상세 주소를 입력해 주세요"
                  value={addressDetail}
                  onChange={(e) => setAddressDetail(e.target.value)}
                  autoComplete="address-line2"
                />
              </label>
            </div>

            <div className="guardian-field">
              <span className="guardian-field__label">
                서명 <i aria-hidden>*</i>
              </span>
              <SignaturePad value={signatureDataUrl} onChange={setSignatureDataUrl} />
            </div>

            <div className="guardian-field">
              <span className="guardian-field__label">
                첨부 : 가족관계증명서 1부 <i aria-hidden>*</i>
              </span>
              <input
                ref={certInputRef}
                className="visually-hidden"
                type="file"
                accept={CERT_ACCEPT}
                onChange={(e) => onCertChange(e.target.files?.[0] ?? null)}
              />
              <button
                className={`guardian-upload ${certFile ? "is-filled" : ""}`}
                type="button"
                onClick={() => certInputRef.current?.click()}
                aria-label="가족관계증명서 첨부"
              >
                {certPreviewUrl ? (
                  <img src={certPreviewUrl} alt="" />
                ) : certFile ? (
                  <span className="guardian-upload__name">{certFile.name}</span>
                ) : (
                  <span className="guardian-upload__plus" aria-hidden>
                    +
                  </span>
                )}
              </button>
              {certFile && (
                <button
                  className="guardian-upload__clear"
                  type="button"
                  onClick={() => {
                    onCertChange(null);
                    if (certInputRef.current) {
                      certInputRef.current.value = "";
                    }
                  }}
                >
                  첨부 제거
                </button>
              )}
            </div>

            <button className="guardian-submit" type="submit" disabled={submitting}>
              {submitting ? "제출 중..." : "확인"}
            </button>
          </form>
        )}
      </main>

      {previewOpen && (
        <div className="guardian-modal" role="dialog" aria-modal="true" aria-label="근로계약 전문">
          <div className="guardian-modal__backdrop" onClick={() => setPreviewOpen(false)} />
          <div className="guardian-modal__panel">
            <div className="guardian-modal__head">
              <h2>근로계약 전문</h2>
              <button type="button" onClick={() => setPreviewOpen(false)} aria-label="닫기">
                ×
              </button>
            </div>
            <div className="guardian-modal__body">
              {previewLoading ? "불러오는 중..." : previewText || "표시할 전문이 없습니다."}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`guardian-toast guardian-toast--${toast.type}`} role="status">
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default GuardianConsentPage;
